import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { engagementConfig, pendingChest } from '../data/engagement';
import { getRecapConfig } from '../hooks/recapConfigStore';
import type { HiveChestReward, ReminderFrequency } from '../types/engagement';

/**
 * Engagement preferences and prototype state.
 * Everything optional starts OFF; nothing here changes Life Score or the
 * 100 Bee Daily Check base. Production integrity: REQUIRES BACKEND.
 */
interface EngagementValue {
  /* Smart reminders */
  smartTiming: boolean;
  setSmartTiming: (on: boolean) => void;
  frequency: ReminderFrequency;
  setFrequency: (value: ReminderFrequency) => void;
  quietHours: boolean;
  setQuietHours: (on: boolean) => void;

  /* Streak protection */
  protectionEnabled: boolean;
  setProtectionEnabled: (on: boolean) => void;
  protectionCharges: number;

  /* Baú da Colmeia */
  chest: HiveChestReward | null;
  chestOpened: boolean;
  openChest: () => void;
  chestBonusBee: number;

  /* Community challenges */
  joinedChallenges: string[];
  toggleChallenge: (id: string) => void;

  /* Weekly league */
  leagueOptIn: boolean;
  setLeagueOptIn: (on: boolean) => void;

  /* Recap share bonus — max one per recap; bonus Bee never counts toward the Post unlock */
  rewardedRecaps: string[];
  /** Returns true only for the first CONFIRMED share of a recap (never on intent alone). */
  claimRecapShareReward: (recapId: string) => boolean;
  recapBonusBee: number;
}
const EngagementContext = createContext<EngagementValue | null>(null);
export function EngagementProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [smartTiming, setSmartTiming] = useState(true);
  const [frequency, setFrequency] = useState<ReminderFrequency>('balanced');
  const [quietHours, setQuietHours] = useState(true);
  const [protectionEnabled, setProtectionEnabled] = useState<boolean>(engagementConfig.streakProtection.defaultEnabled);
  const [chestOpened, setChestOpened] = useState(false);
  const [joinedChallenges, setJoined] = useState<string[]>(['ch-running-40', 'ch-running-consistency']);
  const [leagueOptIn, setLeagueOptIn] = useState<boolean>(engagementConfig.league.defaultOptIn);

  // Recap id → bonus Bee granted (amount frozen at grant time). Triggered only by a
  // CONFIRMED share; in the prototype that is the explicit demo simulation.
  const [rewardedMap, setRewardedMap] = useState<Record<string, number>>({});
  const rewardedRef = React.useRef<Record<string, number>>({});
  const claimRecapShareReward = useCallback((recapId: string) => {
    if (recapId in rewardedRef.current) return false;
    rewardedRef.current = {
      ...rewardedRef.current,
      [recapId]: getRecapConfig().shareRewardBee
    };
    setRewardedMap(rewardedRef.current);
    return true;
  }, []);
  const rewardedRecaps = useMemo(() => Object.keys(rewardedMap), [rewardedMap]);
  const openChest = useCallback(() => setChestOpened(true), []);
  const toggleChallenge = useCallback((id: string) => setJoined(list => list.includes(id) ? list.filter(item => item !== id) : [...list, id]), []);
  const value = useMemo<EngagementValue>(() => ({
    smartTiming,
    setSmartTiming,
    frequency,
    setFrequency,
    quietHours,
    setQuietHours,
    protectionEnabled,
    setProtectionEnabled,
    protectionCharges: protectionEnabled ? engagementConfig.streakProtection.maxCharges : 0,
    chest: pendingChest,
    chestOpened,
    openChest,
    chestBonusBee: chestOpened && pendingChest ? pendingChest.bonusBee : 0,
    joinedChallenges,
    toggleChallenge,
    leagueOptIn,
    setLeagueOptIn,
    rewardedRecaps,
    claimRecapShareReward,
    recapBonusBee: Object.values(rewardedMap).reduce((sum, n) => sum + n, 0)
  }), [smartTiming, frequency, quietHours, protectionEnabled, chestOpened, openChest, joinedChallenges, toggleChallenge, leagueOptIn, rewardedRecaps, rewardedMap, claimRecapShareReward]);
  return <EngagementContext.Provider value={value} data-id="element-1561" data-mp="1es7qodjo7nqp">{children}</EngagementContext.Provider>;
}
export function useEngagement(): EngagementValue {
  const value = useContext(EngagementContext);
  if (!value) throw new Error('useEngagement must be used inside EngagementProvider');
  return value;
}